<template>
  <div class="h-full w-full bg-white shadow-lg rounded-lg p-6">
    <h1 class="text-2xl sm:text-4xl title mb-10">Top Consultores Iniciantes</h1>
    <RankingTableV2
      :headers="tableHeaders"
      :data="displayedData"
      :rankingField="rankingField"
      :order-descending="true"
      :is-presentation-mode="isPresentationMode"
      @openModal="handleOpenModal"
      @openCanceledModal="handleCanceledModal"
    />
    <ModalComponent
      fullscreen
      :is-visible.sync="isSalesBilletsModalVisible"
      :modal-data="salesBilletsData"
      title="Vendas Pagas"
    />
    <ModalComponent
      fullscreen
      :is-visible.sync="isModalVisible"
      :modal-data="openPixData"
      title="Pix em Aberto"
    />
    <ModalComponent
      fullscreen
      :is-visible.sync="isCanceledBilletsModalVisible"
      :modal-data="canceledBilletsData"
      title="Boletos Cancelados"
    />
  </div>
</template>

<script>
import services from '../../../../services';
import { deepClone } from '../../../../utils/deepClone';
import ModalComponent from '../components/ModalComponent.vue';
import RankingTableV2 from '../components/RankingTableV2.vue';
import { headers } from '../mocks/dataMocksTopConsultor';
import { getStartEndDate } from '../utils';
import {
  calculateWorkingDays,
  totalWorkingDaysInDate,
} from '../utils/eh-dia-util';

export default {
  name: 'TopInitialConsultores',
  components: { RankingTableV2, ModalComponent },
  data() {
    return {
      tableHeaders: headers,
      tableData: [],
      rankingField: 'totalVendas',
      tableDataPart1: [],
      tableDataPart2: [],
      displayedData: [],
      displayPart: 1,
      interval: null,
      openPixData: null,
      isModalVisible: false,
      salesBilletsData: null,
      isSalesBilletsModalVisible: false,
      canceledBilletsData: null,
      isCanceledBilletsModalVisible: false,
      temporaryTables: null,
    };
  },
  props: {
    previousData: Array,
    finalDate: String,
    isPresentationMode: Boolean,
    dataSend: Object,
  },
  watch: {
    tableData(newTableData) {
      const MAX_ITEMS_BEFORE_SPLIT = 19;

      const shouldSplitData =
        this.isPresentationMode && newTableData.length > MAX_ITEMS_BEFORE_SPLIT;
      if (shouldSplitData) {
        const middleIndex = Math.ceil(newTableData.length / 2);
        this.tableDataPart1 = newTableData.slice(0, middleIndex);
        this.tableDataPart2 = newTableData.slice(middleIndex);
        this.displayedData = this.tableDataPart1;
        return;
      }

      this.displayedData = newTableData;
    },
    isPresentationMode(newVal) {
      if (newVal) {
        this.setupDataSwitchingInterval();
        return;
      }

      this.displayPart = 1;
      this.displayedData = this.tableData;
      this.clearSetupDataSwitchingInterval();
    },
  },
  methods: {
    async fetchData() {
      try {
        const model = 'painel-comercial';
        const action = `getTopConsultoresIniciantesComercial`;

        const dateWithTime = this.finalDate + 'T00:00:00';
        const date = new Date(dateWithTime);
        const { startDate, endDate } = getStartEndDate(this.finalDate);

        const currentRelogio = calculateWorkingDays(date);
        const totalRelogioMonth = totalWorkingDaysInDate(date);
        const res = await services.post(model, action, {
          currentRelogio,
          totalRelogioMonth,
          startDate,
          endDate,
          ...this.temporaryTables,
          ...this.dataSend,
        });

        const localPreviousData = this.previousData
          ? [...this.previousData]
          : [];

        const newData = res.data;
        const hasPreviousData = localPreviousData.length > 0;
        const hasNewData = newData && newData.length > 0;

        const processedData = hasNewData
          ? deepClone(newData).map((item) => {
              const { totalVendas, relogio } = item;

              const performance = this.evaluatePerformance(
                totalVendas,
                relogio,
              );
              return {
                ...item,
                performance,
              };
            })
          : [];

        const isRankingChanged =
          hasPreviousData &&
          hasNewData &&
          newData.some((newItem, index) => {
            const { top: newTop } = newItem;
            const previousItem = localPreviousData[index];
            return previousItem && newTop !== previousItem.top;
          });

        if (isRankingChanged) {
          this.localPreviousData = [...newData];
          newData.forEach((item, index) => {
            const { nome, top } = item;
            const positionChange = this.compareRanking(
              { nome, top },
              localPreviousData,
            );
            processedData[index].positionChange = positionChange;
            this.$emit('update-data', processedData);
          });
        }

        this.tableData = processedData;
      } catch (error) {
        console.error(
          'Erro ao buscar classificações de relacionamento:',
          error,
        );
      }
    },
    compareRanking(item, previousData) {
      const previousItem = previousData.find(({ nome }) => nome === item.nome);
      if (!previousItem) {
        return;
      }

      const previousPosition = parseInt(previousItem.top.replace('º', ''));
      const currentPosition = parseInt(item.top.replace('º', ''));
      return currentPosition - previousPosition;
    },
    evaluatePerformance(totalVendas, relogio) {
      const PERFORMANCE_VALUE = Math.floor(relogio * 0.9);

      if (totalVendas >= relogio) {
        return 'good';
      }

      if (totalVendas < PERFORMANCE_VALUE) {
        return 'bad';
      }

      return 'meh';
    },
    switchDataPart() {
      const shouldSwitch =
        this.isPresentationMode && this.tableDataPart2.length > 0;
      if (!shouldSwitch) {
        return;
      }

      this.displayPart = this.displayPart === 1 ? 2 : 1;

      this.displayedData =
        this.displayPart === 1 ? this.tableDataPart1 : this.tableDataPart2;
    },

    setupDataSwitchingInterval() {
      const SWITCH_INTERVAL_VALUE = 10000;
      this.clearSetupDataSwitchingInterval();
      this.interval = setInterval(this.switchDataPart, SWITCH_INTERVAL_VALUE);
    },
    clearSetupDataSwitchingInterval() {
      clearInterval(this.interval);
      this.interval = null;
    },
    async handleOpenModal(rowData, onlyOpen = true) {
      this.$vs.loading();
      await this.createTemporaryTables();
      await this.fetchGetOpenBillets(rowData, onlyOpen);
      this.$vs.loading.close();
      this.dropTemporaryTables();
    },
    async handleCanceledModal(rowData) {
      this.$vs.loading();
      await this.createTemporaryTables();
      await this.fetchCanceledBillets(rowData);
      this.$vs.loading.close();
      this.dropTemporaryTables();
    },
    closeModal() {
      this.$emit('update:isModalVisible', false);
    },
    async fetchGetOpenBillets(openPixData, onlyOpen) {
      try {
        const { startDate, endDate } = getStartEndDate(this.finalDate);
        const idUsuario = openPixData.idUsuario;
        const isSupervisor = false;

        const res = await services.post('painel-comercial', 'getOpenBillets', {
          startDate,
          endDate,
          idUsuario,
          isSupervisor,
          onlyOpenSituation: onlyOpen,
          ...this.temporaryTables,
          ...this.dataSend,
        });

        if (onlyOpen) {
          this.openPixData = res.data;
          this.isModalVisible = true;
          return;
        }

        this.salesBilletsData = res.data;
        this.isSalesBilletsModalVisible = true;
      } catch (error) {
        console.error(
          'Erro ao buscar boletos abertos para melhores consultores:',
          error,
        );
      }
    },
    async fetchCanceledBillets(openPixData) {
      try {
        const { startDate, endDate } = getStartEndDate(this.finalDate);
        const idUsuario = openPixData.idUsuario;
        const isSupervisor = false;

        const res = await services.post(
          'painel-comercial',
          'getCanceledBillets',
          {
            startDate,
            endDate,
            idUsuario,
            isSupervisor,
            ...this.temporaryTables,
            ...this.dataSend,
          },
        );

        this.canceledBilletsData = res.data;
        this.isCanceledBilletsModalVisible = true;
      } catch (error) {
        console.error(
          'Erro ao buscar boletos cancelados para melhores consultores:',
          error,
        );
      }
    },
    async createTemporaryTables() {
      try {
        const { startDate, endDate } = getStartEndDate(this.finalDate);
        const { data } = await services.post(
          'painel-comercial',
          'createTemporaryTables',
          {
            startDate,
            endDate,
            ...this.dataSend,
          },
        );
        this.temporaryTables = data;
      } catch (error) {
        console.error('Erro ao criar tabelas tempor:', error);
      }
    },
    async dropTemporaryTables() {
      await services.post(
        'painel-comercial',
        'dropTemporaryTables',
        this.temporaryTables,
      );
      this.temporaryTables = null;
    },
  },
  async mounted() {
    await this.createTemporaryTables();
    await this.fetchData();
    if (this.isPresentationMode) {
      this.setupDataSwitchingInterval();
    }
    this.dropTemporaryTables();
  },
  beforeDestroy() {
    this.clearSetupDataSwitchingInterval();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400&display=swap');

.title {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  color: #434343;
}
</style>
